import React from 'react';
import { useFetcher } from 'react-router-dom';

/**
 * Bulk-archive plumbing for library list pages. The caller passes its archive
 * route (e.g. `/app/library/styles/archive`) and a JSON body key; we submit
 * through a router fetcher so the parent loader revalidates without a
 * navigation.
 *
 * The hook owns the confirm-dialog visibility and the post-archive cleanup.
 * Actions are expected to return `{ failedIds?: string[] }` when partial
 * failure is possible (custom forms fan out per-id mutations); when present,
 * `onArchived` is *not* called and the failing rows remain selected so the
 * user can retry. Actions that succeed atomically just return nothing and the
 * happy path runs unchanged.
 */
export function useLibraryBulkArchive(params: {
  /** Absolute action path, e.g. `/app/library/forms/archive`. */
  actionPath: string;
  /** JSON key under which to submit the IDs (defaults to `templateIds`). */
  payloadKey?: string;
  /**
   * Called once the action settles with no partial failure. Pages use this to
   * clear their row selection so the floating bar dismisses. `ids` are the ones
   * submitted for this request.
   */
  onArchived?: (result: { failedIds: string[]; ids: string[] }) => void;
}) {
  const fetcher = useFetcher<{ failedIds?: string[] }>();
  const { actionPath, payloadKey = 'templateIds', onArchived } = params;

  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [failedIds, setFailedIds] = React.useState<string[]>([]);
  // True between `archive()` and the next idle that follows it; lets the
  // effect below distinguish a real action result from `fetcher.data` left
  // over from a previous submission or another fetcher in the route.
  const pendingRef = React.useRef(false);
  const submittedIdsRef = React.useRef<string[]>([]);

  const archive = React.useCallback(
    (ids: string[]) => {
      pendingRef.current = true;
      submittedIdsRef.current = ids;
      setFailedIds([]);
      fetcher.submit(
        { [payloadKey]: ids },
        { method: 'POST', action: actionPath, encType: 'application/json' }
      );
    },
    [actionPath, fetcher, payloadKey]
  );

  const isArchiving =
    fetcher.state === 'submitting' || (fetcher.state === 'loading' && pendingRef.current);

  React.useEffect(() => {
    if (fetcher.state !== 'idle' || !pendingRef.current) return;
    pendingRef.current = false;
    setDialogOpen(false);
    const nextFailedIds = fetcher.data?.failedIds ?? [];
    setFailedIds(nextFailedIds);
    if (nextFailedIds.length === 0) {
      onArchived?.({ failedIds: [], ids: submittedIdsRef.current });
    }
    // When there are failures we leave selection intact so the user can retry.
  }, [fetcher.data, fetcher.state, onArchived]);

  const openDialog = React.useCallback(() => {
    setFailedIds([]);
    setDialogOpen(true);
  }, []);
  const closeDialog = React.useCallback(() => setDialogOpen(false), []);
  const clearFailedIds = React.useCallback(() => setFailedIds([]), []);

  return {
    archive,
    isArchiving,
    dialogOpen,
    openDialog,
    closeDialog,
    failedIds,
    clearFailedIds
  };
}
